// pageAll — read EVERY row of a Supabase query, not just the first page.
//
// PostgREST caps any select at 1000 rows and says nothing about it. Cases read
// straight through that cap, so Textilefield (1,442 cases) silently showed 1,000 and
// the bucket counts under-reported by 442 — with no error to notice.
//
// `build` must return a FRESH query builder each call (a builder is single-use once
// awaited) and MUST carry a deterministic .order(...); without a stable sort the
// pages can overlap or skip rows.
//
// SPEED: the first page is read alone (most reads end there, in one request). If it is full, the next
// pages are requested at the SAME time, a few at once, until a short page shows the end. The pages are
// put back in order, so the rows are exactly what reading them one after another gives; only the waiting
// is shorter (three pages of the case list used to cost three round trips in a row, now about one).
// A request past the end comes back empty. If ANY page fails the whole read fails: no rows come back, only
// the error (a list that is silently short is exactly the bug this file exists to prevent).
const PAGE_SIZE = 1000;
const PAGE_CONCURRENCY = 4;

export async function pageAll(build, { pageSize = PAGE_SIZE, concurrency = PAGE_CONCURRENCY } = {}) {
  const rows = [];
  const first = await build().range(0, pageSize - 1);
  if (first.error) return { data: [], error: first.error };
  const firstPage = first.data || [];
  rows.push(...firstPage);
  if (firstPage.length < pageSize) return { data: rows, error: null };
  const width = Math.max(1, concurrency);
  for (let page = 1; ; page += width) {
    // eslint-disable-next-line no-await-in-loop
    const batch = await Promise.all(Array.from({ length: width }, (_, i) => {
      const from = (page + i) * pageSize;
      return build().range(from, from + pageSize - 1);
    }));
    for (const res of batch) {
      if (res.error) return { data: [], error: res.error };
      const part = res.data || [];
      rows.push(...part);
      if (part.length < pageSize) return { data: rows, error: null };
    }
  }
}

export default pageAll;
